import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { ChevronRight, ChevronLeft, Car, Home as HomeIcon, Activity, HeartPulse, Briefcase, Zap, Bike, Plane, PawPrint, Check, CheckCircle2, Loader2, Upload, X, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api, formatApiError } from "../lib/api";

const STEPS = ["Tipologia", "Dati", "Contatti", "Invio"];

const TYPES = [
  { key: "auto",           label: "Auto",                  icon: Car,        desc: "Preventivo RCA e garanzie accessorie" },
  { key: "casa",           label: "Casa",                  icon: HomeIcon,   desc: "Proteggi abitazione e contenuto" },
  { key: "vita",           label: "Vita",                  icon: HeartPulse, desc: "Protezione della famiglia" },
  { key: "infortuni",      label: "Infortuni e Malattia",  icon: Activity,   desc: "Tutela salute e reddito" },
  { key: "rc_pro",         label: "RC Professionale",      icon: Briefcase,  desc: "Per professionisti e imprese" },
  { key: "moto",           label: "Moto",                  icon: Bike,       desc: "RCA e protezioni dedicate" },
  { key: "monopattino",    label: "Monopattino Elettrico", icon: Zap,        desc: "RC obbligatoria e garanzie" },
  { key: "viaggi",         label: "Viaggi",                icon: Plane,      desc: "Assistenza e spese mediche" },
  { key: "pet",            label: "Cani e Gatti",          icon: PawPrint,   desc: "Spese veterinarie e RC" },
];

// Costanti per i multiselect delle nuove tipologie
const RC_PRO_GARANZIE     = ["RC Professionale", "Tutela legale", "Cyber", "Colpa grave", "Non so / Vorrei essere consigliato"];
const MONOPATTINO_GARANZIE = ["RC", "Furto", "Infortuni conducente", "Tutela legale", "Assistenza", "Non so / Vorrei essere consigliato"];
const AUTO_GARANZIE = [
  "RCA",
  "Furto",
  "Incendio",
  "Furto e Incendio",
  "Kasko",
  "Collisione / Mini Kasko",
  "Eventi naturali",
  "Grandine",
  "Atti vandalici",
  "Cristalli",
  "Assistenza stradale",
  "Tutela legale",
  "Infortuni conducente",
  "Non so quali scegliere / Vorrei essere consigliato",
];
const MOTO_GARANZIE       = [
  "RCA",
  "Furto",
  "Incendio",
  "Furto e Incendio",
  "Eventi naturali",
  "Assistenza stradale",
  "Tutela legale",
  "Infortuni conducente",
  "Non so quali scegliere / Vorrei essere consigliato",
];
const VIAGGI_GARANZIE     = ["Annullamento", "Spese mediche", "Bagaglio", "Assistenza", "Responsabilità civile", "Non so / Vorrei essere consigliato"];
const TUTELA_AMBITI       = ["Famiglia", "Lavoro", "Circolazione", "Casa", "Professione", "Azienda"];
const PET_GARANZIE        = ["Spese veterinarie", "Responsabilità civile", "Tutela legale", "Assistenza", "Non so / Vorrei essere consigliato"];

const COPERTURE = [
  "Incendio",
  "Furto e rapina",
  "Responsabilità civile della famiglia",
  "Danni da acqua",
  "Cristalli",
  "Atti vandalici",
  "Eventi atmosferici",
  "Terremoto",
  "Assistenza casa",
  "Tutela legale",
  "Danni elettrici",
  "Non so quali scegliere / Vorrei essere consigliato",
];
const PRODOTTI_VITA = [
  "Previdenza (FPA/PIP)",
  "Investimento Multiramo",
  "Temporanea Caso Morte (TCM)",
  "Business (TFM/TFR/Capitalizzazione)",
  "Non so / Vorrei essere consigliato",
];
const GARANZIE_INFORTUNI = [
  "Infortuni",
  "Malattia",
  "Rimborso spese mediche",
  "Indennità da ricovero",
  "Patologie gravi",
  "Invalidità permanente",
  "Prevenzione / Check-up",
  "Non so / Vorrei essere consigliato",
];

const ALLOWED_MIME = ["application/pdf", "image/jpeg", "image/png"];
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_FILES = 3;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = reader.result || "";
      const b64 = String(res).split(",")[1] || "";
      resolve(b64);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function fmtSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function Label({ children, required }) {
  return (
    <label className="text-xs font-medium text-slate-700 tracking-wide uppercase">
      {children} {required && <span className="text-[#C5A059]">*</span>}
    </label>
  );
}
function Input({ testid, ...props }) {
  const readOnlyCls = props.readOnly ? "bg-slate-50 cursor-not-allowed" : "bg-white";
  return (
    <input
      data-testid={testid}
      {...props}
      className={`mt-2 w-full h-11 px-3 ${readOnlyCls} border border-slate-300 focus:border-slate-900 focus:outline-none text-sm`}
    />
  );
}
function Select({ testid, children, ...props }) {
  return (
    <select
      data-testid={testid}
      {...props}
      className="mt-2 w-full h-11 px-3 bg-white border border-slate-300 focus:border-slate-900 focus:outline-none text-sm"
    >
      {children}
    </select>
  );
}
function Textarea({ testid, ...props }) {
  return (
    <textarea
      data-testid={testid}
      {...props}
      className="mt-2 w-full min-h-[96px] px-3 py-2 bg-white border border-slate-300 focus:border-slate-900 focus:outline-none text-sm"
    />
  );
}

export default function Quote() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initialType = params.get("type");
  const initialTypeValid = !!TYPES.find((t) => t.key === initialType);

  // Se l'utente arriva con ?type=X valido (es. da landing page o home card),
  // salta lo step 0 (product selection) e va direttamente al form dedicato.
  const [step, setStep] = useState(initialTypeValid ? 1 : 0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [attachments, setAttachments] = useState([]); // [{file, filename, content_type, size, data_b64}]
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    quote_type: TYPES.find((t) => t.key === initialType)?.key || "",
    auto: {
      // Dati veicolo
      targa: "", marca: "", modello: "", anno: "",
      alimentazione: "", km_annui: "",
      uso_veicolo: "", antifurto: "",
      // Conducente
      conducente_nome: "", conducente_data_nascita: "", guida: "",
      // Proprietario del veicolo
      proprietario_nome: "", proprietario_data_nascita: "", proprietario_codice_fiscale: "",
      // Situazione assicurativa
      decorrenza: "", situazione_veicolo: "", data_consegna_prevista: "",
      // Classe di merito
      provenienza_cu: "", classe_merito: "",
      targa_recupero_cu: "", familiare_nome: "", familiare_parentela: "",
      // Garanzie
      garanzie: ["RCA"],
      note: "",
    },
    casa: {
      // Decorrenza
      decorrenza: "",
      // Immobile
      indirizzo: "", citta: "", cap: "", tipo: "", piano: "",
      mq: "", anno_costruzione: "", uso: "",
      // Richiedente
      richiedente_tipo: "", richiedente_nome: "", richiedente_data_nascita: "", richiedente_codice_fiscale: "",
      // Coperture
      coperture: [],
      // Legacy (mantenuto in state per compat con reset)
      antifurto: false,
      note: "",
    },
    vita: {
      // Persona da assicurare
      nome: "", data_nascita: "", codice_fiscale: "", professione: "",
      // Prodotti
      prodotti: [], obiettivo: "",
      // TCM
      tcm_capitale: "", tcm_durata: "",
      // Previdenza / Multiramo
      versamento_tipo: "", importo_periodico: "", capitale_iniziale: "",
      note: "",
    },
    infortuni: {
      // Persona
      nome: "", data_nascita: "", codice_fiscale: "", professione: "",
      // Situazione
      copertura_per: "", sport_rischio_bool: "",
      // Garanzie
      garanzie: [], massimale_invalidita: "",
      note: "",
    },
    rc_pro: {
      // Richiedente
      nome: "", cognome: "", data_nascita: "", codice_fiscale: "",
      // Attività
      professione: "", ragione_sociale: "", partita_iva: "", fatturato_annuo: "", anni_attivita: "", iscrizione_albo: "", massimale: "",
      // Garanzie
      garanzie: [], note: "",
    },
    monopattino: {
      // Decorrenza
      decorrenza: "",
      // Proprietario
      nome: "", cognome: "", data_nascita: "", codice_fiscale: "",
      // Mezzo
      marca: "", modello: "", targa: "", numero_serie: "", anno_acquisto: "", comune: "", uso_prevalente: "",
      // Legacy
      marca_modello: "",
      // Garanzie
      garanzie: [], note: "",
    },
    moto: {
      // Dati veicolo
      targa: "", marca: "", modello: "", cilindrata: "", anno: "",
      alimentazione: "", km_annui: "",
      uso: "", antifurto: "",
      // Conducente
      conducente_nome: "", conducente_data_nascita: "", guida: "",
      // Proprietario del veicolo
      proprietario_nome: "", proprietario_data_nascita: "", proprietario_codice_fiscale: "",
      // Situazione assicurativa
      decorrenza: "", situazione_veicolo: "", data_consegna_prevista: "",
      // Classe di merito
      provenienza_cu: "", classe_merito: "",
      targa_recupero_cu: "", familiare_nome: "", familiare_parentela: "",
      // Garanzie
      garanzie: ["RCA"],
      note: "",
    },
    viaggi: {
      // Richiedente
      nome: "", cognome: "", data_nascita: "", codice_fiscale: "",
      richiedente_viaggiatore: "",
      // Viaggio
      destinazione: "", data_partenza: "", data_ritorno: "",
      numero_viaggiatori: "", tipologia: "", valore_viaggio: "",
      // Viaggiatori dinamici
      viaggiatori: [],
      // Garanzie
      garanzie: [], note: "",
    },
    tutela_legale: { soggetto: "", ambito: [], necessita: "", copertura: "", note: "" },
    pet: {
      // Proprietario
      proprietario_nome: "", proprietario_cognome: "", proprietario_data_nascita: "", proprietario_codice_fiscale: "",
      // Animale
      animale: "", nome: "", razza: "", data_nascita: "", eta: "", sesso: "",
      microchip_presente: "", microchip_numero: "", stato_salute: "",
      // Garanzie
      garanzie: [], note: "",
    },
    altro: { tipologia: "", descrizione: "" },
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    note: "",
    privacy_accepted: false,
  });

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const showAuto        = form.quote_type === "auto";
  const showCasa        = form.quote_type === "casa";
  const showVita        = form.quote_type === "vita";
  const showInfortuni   = form.quote_type === "infortuni";
  const showRcPro       = form.quote_type === "rc_pro";
  const showMonopattino = form.quote_type === "monopattino";
  const showMoto        = form.quote_type === "moto";
  const showViaggi      = form.quote_type === "viaggi";
  const showTutelaLegale = form.quote_type === "tutela_legale";
  const showPet         = form.quote_type === "pet";
  const showAltro       = form.quote_type === "altro";

  const canProceed = useMemo(() => {
    if (step === 0) return !!form.quote_type;
    if (step === 1) {
      if (showAuto && (!form.auto.marca || !form.auto.modello || !form.auto.proprietario_codice_fiscale)) return false;
      if (showMoto && (!form.moto.marca || !form.moto.modello || !form.moto.proprietario_codice_fiscale)) return false;
      if (showCasa && (!form.casa.indirizzo || !form.casa.citta || !form.casa.richiedente_codice_fiscale)) return false;
      if (showInfortuni && !form.infortuni.codice_fiscale) return false;
      if (showRcPro && !form.rc_pro.codice_fiscale) return false;
      if (showMonopattino && !form.monopattino.codice_fiscale) return false;
      if (showViaggi && !form.viaggi.codice_fiscale) return false;
      if (showPet && !form.pet.proprietario_codice_fiscale) return false;
      if (showVita && !form.vita.codice_fiscale) return false;
      if (showAltro && !form.altro.tipologia) return false;
      return true;
    }
    if (step === 2) {
      return form.nome && form.cognome && form.email && form.telefono && form.privacy_accepted;
    }
    return true;
  }, [step, form, showAuto, showMoto, showCasa, showVita, showInfortuni, showRcPro, showMonopattino, showViaggi, showPet, showAltro]);

  const setField = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) {
        ref[keys[i]] = { ...ref[keys[i]] };
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const toggleCopertura = (c) => {
    setForm((prev) => {
      const has = prev.casa.coperture.includes(c);
      return {
        ...prev,
        casa: {
          ...prev.casa,
          coperture: has ? prev.casa.coperture.filter((x) => x !== c) : [...prev.casa.coperture, c],
        },
      };
    });
  };

  const toggleVitaProdotto = (p) => {
    setForm((prev) => {
      const has = prev.vita.prodotti.includes(p);
      return {
        ...prev,
        vita: {
          ...prev.vita,
          prodotti: has ? prev.vita.prodotti.filter((x) => x !== p) : [...prev.vita.prodotti, p],
        },
      };
    });
  };

  const toggleInfortuniGaranzia = (g) => {
    setForm((prev) => {
      const has = prev.infortuni.garanzie.includes(g);
      return {
        ...prev,
        infortuni: {
          ...prev.infortuni,
          garanzie: has ? prev.infortuni.garanzie.filter((x) => x !== g) : [...prev.infortuni.garanzie, g],
        },
      };
    });
  };

  // Helper generico per array multiselect (rc_pro.garanzie, monopattino.garanzie, ecc.)
  const toggleInArray = (section, field, value) => {
    setForm((prev) => {
      const arr = prev[section][field] || [];
      const has = arr.includes(value);
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: has ? arr.filter((x) => x !== value) : [...arr, value],
        },
      };
    });
  };

  // Sincronizza il numero di viaggiatori con l'array viaggiatori.
  // Se richiedente_viaggiatore="si", precompila il primo viaggiatore con i dati del richiedente.
  const syncViaggiatori = (n, currentForm) => {
    const num = Math.max(0, Math.min(30, Number(n) || 0));
    setForm((prev) => {
      const src = currentForm || prev;
      const existing = src.viaggi.viaggiatori || [];
      const next = [];
      for (let i = 0; i < num; i++) {
        if (i === 0 && src.viaggi.richiedente_viaggiatore === "si") {
          next.push({
            nome: existing[i]?.nome || src.viaggi.nome || "",
            cognome: existing[i]?.cognome || src.viaggi.cognome || "",
            data_nascita: existing[i]?.data_nascita || src.viaggi.data_nascita || "",
            codice_fiscale: existing[i]?.codice_fiscale || src.viaggi.codice_fiscale || "",
          });
        } else {
          next.push(existing[i] || { nome: "", cognome: "", data_nascita: "", codice_fiscale: "" });
        }
      }
      return { ...prev, viaggi: { ...prev.viaggi, numero_viaggiatori: n, viaggiatori: next } };
    });
  };

  const setViaggiatoreField = (idx, field, value) => {
    setForm((prev) => {
      const arr = [...(prev.viaggi.viaggiatori || [])];
      arr[idx] = { ...(arr[idx] || {}), [field]: value };
      return { ...prev, viaggi: { ...prev.viaggi, viaggiatori: arr } };
    });
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (attachments.length + files.length > MAX_FILES) {
      toast.error(`Puoi allegare al massimo ${MAX_FILES} file.`);
      return;
    }
    const added = [];
    for (const f of files) {
      if (!ALLOWED_MIME.includes(f.type)) {
        toast.error(`Tipo file non ammesso: ${f.name}. Sono accettati PDF, JPG e PNG.`);
        continue;
      }
      if (f.size > MAX_FILE_BYTES) {
        toast.error(`${f.name} è troppo grande (max 5 MB).`);
        continue;
      }
      try {
        const data_b64 = await fileToBase64(f);
        added.push({ filename: f.name, content_type: f.type, size: f.size, data_b64 });
      } catch {
        toast.error(`Impossibile leggere ${f.name}`);
      }
    }
    if (added.length) setAttachments((prev) => [...prev, ...added]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const body = {
        quote_type: form.quote_type,
        nome: form.nome,
        cognome: form.cognome,
        email: form.email,
        telefono: form.telefono,
        note: form.note || null,
        privacy_accepted: form.privacy_accepted,
        auto: showAuto
          ? {
              ...form.auto,
              anno: form.auto.anno ? Number(form.auto.anno) : null,
              km_annui: form.auto.km_annui ? Number(form.auto.km_annui) : null,
            }
          : null,
        casa: showCasa
          ? {
              ...form.casa,
              mq: form.casa.mq ? Number(form.casa.mq) : null,
              anno_costruzione: form.casa.anno_costruzione ? Number(form.casa.anno_costruzione) : null,
            }
          : null,
        altro: showAltro ? form.altro : null,
        vita: showVita ? { ...form.vita } : null,
        infortuni: showInfortuni ? { ...form.infortuni } : null,
        rc_pro: showRcPro
          ? {
              ...form.rc_pro,
              partita_iva: form.rc_pro.partita_iva === "" ? null : form.rc_pro.partita_iva === "si",
              iscrizione_albo: form.rc_pro.iscrizione_albo === "" ? null : form.rc_pro.iscrizione_albo === "si",
            }
          : null,
        monopattino: showMonopattino ? form.monopattino : null,
        moto: showMoto
          ? {
              ...form.moto,
              km_annui: form.moto.km_annui ? Number(form.moto.km_annui) : null,
            }
          : null,
        viaggi: showViaggi
          ? {
              ...form.viaggi,
              numero_viaggiatori: form.viaggi.numero_viaggiatori ? Number(form.viaggi.numero_viaggiatori) : null,
            }
          : null,
        tutela_legale: showTutelaLegale ? form.tutela_legale : null,
        pet: showPet ? form.pet : null,
        attachments: attachments.length
          ? attachments.map((a) => ({ filename: a.filename, content_type: a.content_type, data_b64: a.data_b64 }))
          : null,
      };
      const { data } = await api.post("/quotes", body);

      // Registra il consenso privacy su Iubenda Consent Solution (ConsentDB)
      try {
        if (typeof window !== "undefined") {
          window._iub = window._iub || {};
          window._iub.cons_instructions = window._iub.cons_instructions || [];
          window._iub.cons_instructions.push(["submit", {
            subject: { email: form.email },
            consent: {
              legal_notices: [{ identifier: "privacy_policy" }],
            },
          }]);
        }
      } catch (_) { /* consent log best-effort */ }

      setDone(data);
      setStep(3);
      toast.success("Richiesta inviata: ti contatteremo a breve.");

      // GA4 conversion event
      try {
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
          window.gtag("event", "generate_lead", {
            currency: "EUR",
            value: 1,
            quote_type: form.quote_type,
          });
        }
      } catch (_) { /* best-effort */ }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <section className="max-w-4xl mx-auto w-full px-6 md:px-10 pt-12 md:pt-16 pb-24">
        <div className="overline">Preventivo</div>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight text-slate-900">
          Richiedi il tuo preventivo.
        </h1>
        <p className="mt-4 text-slate-600 max-w-2xl">
          Inserisci i dati principali del veicolo, dell'abitazione o della copertura che ti interessa.
          Dopo l'invio ti contatteremo con una proposta personalizzata.
        </p>

        {/* Stepper */}
        <div className="mt-10 flex items-center gap-4" data-testid="quote-stepper">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-4 flex-1 last:flex-none">
              <div
                className={`w-8 h-8 border flex items-center justify-center text-xs font-medium ${
                  i <= step ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-400 border-slate-300"
                }`}
              >
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              <div className={`hidden sm:block text-xs tracking-wide uppercase ${i <= step ? "text-slate-900" : "text-slate-400"}`}>{s}</div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-slate-900" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <div className="mt-10 bg-white border border-slate-200 p-6 md:p-10">
          {/* STEP 0 — Type */}
          {step === 0 && (
            <div>
              <h2 className="font-serif text-2xl text-slate-900">Di che preventivo hai bisogno?</h2>
              <p className="text-sm text-slate-500 mt-2">Seleziona una tipologia per proseguire.</p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
                {TYPES.map((t) => {
                  const Icon = t.icon;
                  const active = form.quote_type === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      data-testid={`type-${t.key}`}
                      onClick={() => {
                        setField("quote_type", t.key);
                        setStep(1);
                      }}
                      className={`group relative text-left p-5 md:p-6 border-2 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                        active
                          ? "border-slate-900 bg-[#FAF9F6]"
                          : "border-slate-200 bg-white hover:border-slate-400"
                      }`}
                    >
                      {/* Checkmark corner */}
                      {active && (
                        <span
                          aria-hidden="true"
                          className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center bg-slate-900 text-white"
                        >
                          <Check size={14} strokeWidth={3} />
                        </span>
                      )}
                      <div className="flex items-center gap-3.5">
                        <span
                          className={`shrink-0 w-10 h-10 flex items-center justify-center transition-colors ${
                            active
                              ? "bg-slate-900 text-[#C5A059]"
                              : "bg-[#FAF9F6] text-slate-900 group-hover:bg-slate-900 group-hover:text-[#C5A059]"
                          }`}
                        >
                          <Icon size={20} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="font-serif text-lg text-slate-900 leading-tight truncate">{t.label}</div>
                          <p className="mt-1 text-[13px] text-slate-500 leading-snug truncate">{t.desc}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 1 — Details */}
          {step === 1 && (
            <div className="space-y-10">
              {showAuto && (
                <div>
                  <h2 className="font-serif text-2xl text-slate-900">Preventivo Auto</h2>
                  <p className="text-sm text-slate-500 mt-2">
                    Compila i campi che conosci — non è tutto obbligatorio. I dati di sinistrosità e classe di merito verranno
                    verificati direttamente dall'agenzia sulle banche dati assicurative.
                  </p>

                  {/* Sezione 1 — Situazione assicurativa */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Situazione assicurativa</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div>
                        <Label>Data decorrenza desiderata</Label>
                        <Input testid="auto-decorrenza" type="date" min={todayISO()} value={form.auto.decorrenza} onChange={(e) => setField("auto.decorrenza", e.target.value)} />
                      </div>
                      <div>
                        <Label>Situazione del veicolo</Label>
                        <Select testid="auto-situazione" value={form.auto.situazione_veicolo} onChange={(e) => setField("auto.situazione_veicolo", e.target.value)}>
                          <option value="">Seleziona…</option>
                          <option value="gia_assicurato">Già assicurato</option>
                          <option value="passaggio_proprieta">Appena acquistato / passaggio di proprietà</option>
                          <option value="nuovo_da_immatricolare">Auto nuova da immatricolare</option>
                          <option value="usata_da_acquistare">Auto usata da acquistare</option>
                          <option value="altro">Altro</option>
                        </Select>
                      </div>
                      {form.auto.situazione_veicolo === "nuovo_da_immatricolare" && (
                        <div>
                          <Label>Data prevista consegna (facoltativa)</Label>
                          <Input testid="auto-consegna" type="date" min={todayISO()} value={form.auto.data_consegna_prevista} onChange={(e) => setField("auto.data_consegna_prevista", e.target.value)} />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sezione 2 — Dati veicolo */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Dati veicolo</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      {form.auto.situazione_veicolo !== "nuovo_da_immatricolare" && (
                        <div><Label>Targa</Label><Input testid="auto-targa" value={form.auto.targa} onChange={(e) => setField("auto.targa", e.target.value.toUpperCase())} placeholder="AB123CD" /></div>
                      )}
                      <div><Label required>Marca</Label><Input testid="auto-marca" value={form.auto.marca} onChange={(e) => setField("auto.marca", e.target.value)} placeholder="Fiat" /></div>
                      <div><Label required>Modello</Label><Input testid="auto-modello" value={form.auto.modello} onChange={(e) => setField("auto.modello", e.target.value)} placeholder="Panda" /></div>
                      <div><Label>Anno di immatricolazione</Label><Input testid="auto-anno" type="number" value={form.auto.anno} onChange={(e) => setField("auto.anno", e.target.value)} placeholder="2020" /></div>
                      <div><Label>Km annui stimati</Label><Input testid="auto-km" type="number" value={form.auto.km_annui} onChange={(e) => setField("auto.km_annui", e.target.value)} placeholder="15000" /></div>
                    </div>
                  </div>

                  {/* Sezione 3 — Classe di merito */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Classe di merito</div>
                    <div className="mt-5 grid gap-5">
                      <div>
                        <Label>Hai un veicolo dal quale recuperare la classe di merito?</Label>
                        <Select testid="auto-provenienza-cu" value={form.auto.provenienza_cu} onChange={(e) => setField("auto.provenienza_cu", e.target.value)}>
                          <option value="">Seleziona…</option>
                          <option value="propria">Sì, un veicolo di mia proprietà</option>
                          <option value="familiare">Sì, un veicolo di un familiare convivente</option>
                          <option value="nessuna">No</option>
                          <option value="non_so">Non lo so</option>
                        </Select>
                      </div>
                      {form.auto.provenienza_cu === "propria" && (
                        <div className="grid sm:grid-cols-2 gap-5">
                          <div><Label>Targa del veicolo</Label><Input testid="auto-cu-targa" value={form.auto.targa_recupero_cu} onChange={(e) => setField("auto.targa_recupero_cu", e.target.value.toUpperCase())} placeholder="AB123CD" /></div>
                          <div><Label>Classe di merito attuale (facoltativa)</Label><Input testid="auto-classe-merito" value={form.auto.classe_merito} onChange={(e) => setField("auto.classe_merito", e.target.value)} placeholder="Es. CU/1" /></div>
                        </div>
                      )}
                      {form.auto.provenienza_cu === "familiare" && (
                        <div className="grid sm:grid-cols-2 gap-5">
                          <div><Label>Targa del veicolo del familiare</Label><Input testid="auto-cu-targa" value={form.auto.targa_recupero_cu} onChange={(e) => setField("auto.targa_recupero_cu", e.target.value.toUpperCase())} placeholder="AB123CD" /></div>
                          <div><Label>Nome e cognome del familiare</Label><Input testid="auto-cu-familiare" value={form.auto.familiare_nome} onChange={(e) => setField("auto.familiare_nome", e.target.value)} placeholder="Mario Rossi" /></div>
                          <div className="sm:col-span-2">
                            <Label>Grado di parentela</Label>
                            <Select testid="auto-cu-parentela" value={form.auto.familiare_parentela} onChange={(e) => setField("auto.familiare_parentela", e.target.value)}>
                              <option value="">Seleziona…</option>
                              <option value="coniuge">Coniuge / Convivente</option>
                              <option value="genitore">Genitore</option>
                              <option value="figlio">Figlio/a</option>
                              <option value="fratello">Fratello / Sorella</option>
                              <option value="altro">Altro convivente</option>
                            </Select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sezione 4 — Conducente */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Conducente</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div><Label>Conducente principale</Label><Input testid="auto-conducente" value={form.auto.conducente_nome} onChange={(e) => setField("auto.conducente_nome", e.target.value)} placeholder="Nome Cognome" /></div>
                      <div><Label>Data di nascita conducente</Label><Input testid="auto-nascita" type="date" value={form.auto.conducente_data_nascita} onChange={(e) => setField("auto.conducente_data_nascita", e.target.value)} /></div>
                      <div>
                        <Label>Tipo di guida</Label>
                        <Select testid="auto-guida" value={form.auto.guida} onChange={(e) => setField("auto.guida", e.target.value)}>
                          <option value="">Seleziona…</option>
                          <option value="libera">Guida libera</option>
                          <option value="esperta">Guida esperta</option>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Sezione — Proprietario del veicolo */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Proprietario del veicolo</div>
                    <p className="mt-3 text-sm text-slate-500">Se coincide con il conducente principale puoi ripetere i dati.</p>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div><Label>Nome e cognome</Label><Input testid="auto-prop-nome" value={form.auto.proprietario_nome} onChange={(e) => setField("auto.proprietario_nome", e.target.value)} placeholder="Nome Cognome" /></div>
                      <div><Label>Data di nascita</Label><Input testid="auto-prop-nascita" type="date" value={form.auto.proprietario_data_nascita} onChange={(e) => setField("auto.proprietario_data_nascita", e.target.value)} /></div>
                      <div className="sm:col-span-2"><Label required>Codice fiscale</Label><Input testid="auto-prop-cf" value={form.auto.proprietario_codice_fiscale} onChange={(e) => setField("auto.proprietario_codice_fiscale", e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501U" maxLength={16} /></div>
                    </div>
                  </div>

                  {/* Sezione 5 — Garanzie richieste */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Garanzie richieste</div>
                    <p className="mt-3 text-sm text-slate-500">Seleziona una o più garanzie di tuo interesse. RCA è la copertura obbligatoria di base.</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {AUTO_GARANZIE.map((g) => {
                        const active = form.auto.garanzie.includes(g);
                        return (
                          <button
                            key={g}
                            type="button"
                            data-testid={`auto-gar-${g}`}
                            onClick={() => toggleInArray("auto", "garanzie", g)}
                            className={`px-4 h-10 text-sm border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`}
                          >
                            {g}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sezione 6 — Note */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Note aggiuntive</div>
                    <div className="mt-5">
                      <Textarea testid="auto-note" value={form.auto.note} onChange={(e) => setField("auto.note", e.target.value)} placeholder="Altre informazioni utili (secondo conducente, esigenze particolari, ecc.)" />
                    </div>
                  </div>
                </div>
              )}

              {showCasa && (
                <div>
                  <h2 className="font-serif text-2xl text-slate-900">Preventivo Casa</h2>
                  <p className="text-sm text-slate-500 mt-2">Compila i dati che conosci sull'immobile — nessun campo tecnico obbligatorio.</p>

                  {/* Sezione 1 — Decorrenza */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Decorrenza</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div>
                        <Label>Data decorrenza desiderata</Label>
                        <Input testid="casa-decorrenza" type="date" min={todayISO()} value={form.casa.decorrenza} onChange={(e) => setField("casa.decorrenza", e.target.value)} />
                      </div>
                    </div>
                  </div>

                  {/* Sezione 2 — L'immobile */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">L'immobile</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div className="sm:col-span-2"><Label required>Indirizzo</Label><Input testid="casa-indirizzo" value={form.casa.indirizzo} onChange={(e) => setField("casa.indirizzo", e.target.value)} placeholder="Via Roma 1" /></div>
                      <div><Label required>Città</Label><Input testid="casa-citta" value={form.casa.citta} onChange={(e) => setField("casa.citta", e.target.value)} placeholder="Milano" /></div>
                      <div><Label>CAP</Label><Input testid="casa-cap" value={form.casa.cap} onChange={(e) => setField("casa.cap", e.target.value)} placeholder="20100" /></div>
                      <div>
                        <Label>Tipologia immobile</Label>
                        <Select testid="casa-tipo" value={form.casa.tipo} onChange={(e) => setField("casa.tipo", e.target.value)}>
                          <option value="">Seleziona…</option>
                          <option value="appartamento">Appartamento</option>
                          <option value="villa">Villa</option>
                          <option value="villetta">Villetta a schiera</option>
                          <option value="altro">Altro</option>
                        </Select>
                      </div>
                      {form.casa.tipo === "appartamento" && (
                        <div><Label>Piano</Label><Input testid="casa-piano" value={form.casa.piano} onChange={(e) => setField("casa.piano", e.target.value)} placeholder="Es. Terzo" /></div>
                      )}
                      <div><Label>Metri quadri</Label><Input testid="casa-mq" type="number" value={form.casa.mq} onChange={(e) => setField("casa.mq", e.target.value)} placeholder="90" /></div>
                      <div><Label>Anno di costruzione o ristrutturazione</Label><Input testid="casa-anno" type="number" value={form.casa.anno_costruzione} onChange={(e) => setField("casa.anno_costruzione", e.target.value)} placeholder="1990" /></div>
                      <div className="sm:col-span-2">
                        <Label>Uso dell'immobile</Label>
                        <Select testid="casa-uso" value={form.casa.uso} onChange={(e) => setField("casa.uso", e.target.value)}>
                          <option value="">Seleziona…</option>
                          <option value="abitazione_principale">Abitazione principale</option>
                          <option value="seconda_casa">Seconda casa</option>
                          <option value="locata">Locata a terzi</option>
                          <option value="bnb">B&B / Affitto breve</option>
                          <option value="saltuario">Uso saltuario</option>
                          <option value="altro">Altro</option>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Sezione 3 — Il richiedente */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Il richiedente</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div className="sm:col-span-2">
                        <Label>Il richiedente è</Label>
                        <Select testid="casa-richiedente-tipo" value={form.casa.richiedente_tipo} onChange={(e) => setField("casa.richiedente_tipo", e.target.value)}>
                          <option value="">Seleziona…</option>
                          <option value="proprietario">Proprietario</option>
                          <option value="inquilino">Inquilino</option>
                          <option value="altro">Altro</option>
                        </Select>
                      </div>
                      <div><Label>Nome e cognome</Label><Input testid="casa-richiedente-nome" value={form.casa.richiedente_nome} onChange={(e) => setField("casa.richiedente_nome", e.target.value)} placeholder="Nome Cognome" /></div>
                      <div><Label>Data di nascita</Label><Input testid="casa-richiedente-nascita" type="date" value={form.casa.richiedente_data_nascita} onChange={(e) => setField("casa.richiedente_data_nascita", e.target.value)} /></div>
                      <div className="sm:col-span-2"><Label required>Codice fiscale</Label><Input testid="casa-richiedente-cf" value={form.casa.richiedente_codice_fiscale} onChange={(e) => setField("casa.richiedente_codice_fiscale", e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501U" maxLength={16} /></div>
                    </div>
                  </div>

                  {/* Sezione 4 — Coperture richieste */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Coperture richieste</div>
                    <p className="mt-3 text-sm text-slate-500">Seleziona una o più coperture di tuo interesse.</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {COPERTURE.map((c) => {
                        const active = form.casa.coperture.includes(c);
                        return (
                          <button
                            key={c}
                            type="button"
                            data-testid={`cop-${c}`}
                            onClick={() => toggleCopertura(c)}
                            className={`px-4 h-10 text-sm border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`}
                          >
                            {c}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sezione 5 — Note */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Note aggiuntive</div>
                    <div className="mt-5">
                      <Textarea testid="casa-note" value={form.casa.note} onChange={(e) => setField("casa.note", e.target.value)} placeholder="Altre informazioni utili sull'immobile o sulle coperture desiderate" />
                    </div>
                  </div>
                </div>
              )}

              {showAltro && (
                <div>
                  <h2 className="font-serif text-2xl text-slate-900">Altra copertura</h2>
                  <div className="mt-6 grid gap-5">
                    <div>
                      <Label required>Tipologia copertura</Label>
                      <Input
                        testid="altro-tipo"
                        value={form.altro.tipologia}
                        onChange={(e) => setField("altro.tipologia", e.target.value)}
                        placeholder="Es. Tutela legale, Viaggio, Animali domestici…"
                      />
                    </div>
                    <div>
                      <Label>Descrizione</Label>
                      <Textarea
                        testid="altro-desc"
                        value={form.altro.descrizione}
                        onChange={(e) => setField("altro.descrizione", e.target.value)}
                        placeholder="Descrivi le tue esigenze"
                      />
                    </div>
                  </div>
                </div>
              )}

              {showInfortuni && (
                <div>
                  <h2 className="font-serif text-2xl text-slate-900">Preventivo Infortuni e Malattia</h2>
                  <p className="text-sm text-slate-500 mt-2">Poche informazioni essenziali per orientare la proposta. Nessun dato sanitario dettagliato richiesto in questa fase.</p>

                  {/* Sezione 1 — Persona da assicurare */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Persona da assicurare</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div><Label>Nome e cognome</Label><Input testid="infortuni-nome" value={form.infortuni.nome} onChange={(e) => setField("infortuni.nome", e.target.value)} placeholder="Nome Cognome" /></div>
                      <div><Label>Data di nascita</Label><Input testid="infortuni-nascita" type="date" value={form.infortuni.data_nascita} onChange={(e) => setField("infortuni.data_nascita", e.target.value)} /></div>
                      <div><Label required>Codice fiscale</Label><Input testid="infortuni-cf" value={form.infortuni.codice_fiscale} onChange={(e) => setField("infortuni.codice_fiscale", e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501U" maxLength={16} /></div>
                      <div className="sm:col-span-2"><Label>Professione</Label><Input testid="infortuni-professione" value={form.infortuni.professione} onChange={(e) => setField("infortuni.professione", e.target.value)} placeholder="Es. Avvocato, Operaio, Insegnante…" /></div>
                    </div>
                  </div>

                  {/* Sezione 2 — Situazione */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Situazione</div>
                    <div className="mt-5 grid gap-5">
                      <div>
                        <Label>Chi desideri assicurare?</Label>
                        <Select testid="infortuni-copertura-per" value={form.infortuni.copertura_per} onChange={(e) => setField("infortuni.copertura_per", e.target.value)}>
                          <option value="">Seleziona…</option>
                          <option value="solo_me">Solo me</option>
                          <option value="me_coniuge">Me + coniuge / convivente</option>
                          <option value="nucleo">Intero nucleo familiare</option>
                          <option value="figlio">Figlio/a</option>
                          <option value="altro">Altro</option>
                        </Select>
                      </div>
                      <div>
                        <Label>Pratichi attività sportive considerate a rischio?</Label>
                        <Select testid="infortuni-sport-rischio" value={form.infortuni.sport_rischio_bool} onChange={(e) => setField("infortuni.sport_rischio_bool", e.target.value)}>
                          <option value="">Seleziona…</option>
                          <option value="si">Sì</option>
                          <option value="no">No</option>
                          <option value="non_so">Non so</option>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Sezione 3 — Garanzie */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Garanzie d'interesse</div>
                    <p className="mt-3 text-sm text-slate-500">Seleziona una o più garanzie di tuo interesse.</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {GARANZIE_INFORTUNI.map((g) => {
                        const active = form.infortuni.garanzie.includes(g);
                        return (
                          <button
                            key={g}
                            type="button"
                            data-testid={`infortuni-gar-${g}`}
                            onClick={() => toggleInfortuniGaranzia(g)}
                            className={`px-4 h-10 text-sm border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`}
                          >
                            {g}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sezione 4 — Massimale */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Massimale invalidità</div>
                    <div className="mt-5">
                      <Label>Massimale invalidità desiderato (facoltativo)</Label>
                      <Select testid="infortuni-massimale" value={form.infortuni.massimale_invalidita} onChange={(e) => setField("infortuni.massimale_invalidita", e.target.value)}>
                        <option value="">Seleziona…</option>
                        <option value="100k">Fino a €100.000</option>
                        <option value="100_250k">€100.000 – €250.000</option>
                        <option value="250_500k">€250.000 – €500.000</option>
                        <option value="500k_plus">Oltre €500.000</option>
                        <option value="da_valutare">Da valutare in consulenza</option>
                      </Select>
                    </div>
                  </div>

                  {/* Sezione 5 — Note */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Note aggiuntive</div>
                    <div className="mt-5">
                      <Textarea testid="infortuni-note" value={form.infortuni.note} onChange={(e) => setField("infortuni.note", e.target.value)} placeholder="Polizze già attive, esigenze particolari…" />
                    </div>
                  </div>
                </div>
              )}

              {showVita && (
                <div>
                  <h2 className="font-serif text-2xl text-slate-900">Preventivo Vita</h2>
                  <p className="text-sm text-slate-500 mt-2">Raccogliamo le informazioni essenziali per orientarti sul prodotto più adatto.</p>

                  {/* Sezione 1 — Persona da assicurare */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Persona da assicurare</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div><Label>Nome e cognome</Label><Input testid="vita-nome" value={form.vita.nome} onChange={(e) => setField("vita.nome", e.target.value)} placeholder="Nome Cognome" /></div>
                      <div><Label>Data di nascita</Label><Input testid="vita-nascita" type="date" value={form.vita.data_nascita} onChange={(e) => setField("vita.data_nascita", e.target.value)} /></div>
                      <div><Label required>Codice fiscale</Label><Input testid="vita-cf" value={form.vita.codice_fiscale} onChange={(e) => setField("vita.codice_fiscale", e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501U" maxLength={16} /></div>
                      <div className="sm:col-span-2"><Label>Professione</Label><Input testid="vita-professione" value={form.vita.professione} onChange={(e) => setField("vita.professione", e.target.value)} placeholder="Es. Ingegnere, Insegnante, Commerciante…" /></div>
                    </div>
                  </div>

                  {/* Sezione 2 — Prodotti d'interesse */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Prodotti d'interesse</div>
                    <p className="mt-3 text-sm text-slate-500">Puoi selezionarne uno o più. Se non sei sicuro, seleziona "Non so".</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {PRODOTTI_VITA.map((p) => {
                        const active = form.vita.prodotti.includes(p);
                        return (
                          <button
                            key={p}
                            type="button"
                            data-testid={`vita-prod-${p}`}
                            onClick={() => toggleVitaProdotto(p)}
                            className={`px-4 h-10 text-sm border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`}
                          >
                            {p}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-5">
                      <Label>Obiettivo</Label>
                      <Input testid="vita-obiettivo" value={form.vita.obiettivo} onChange={(e) => setField("vita.obiettivo", e.target.value)} placeholder="Es. Integrazione pensione, protezione famiglia, accumulo capitale…" />
                    </div>
                  </div>

                  {/* Sezione 3 — Condizionale TCM */}
                  {form.vita.prodotti.includes("Temporanea Caso Morte (TCM)") && (
                    <div className="mt-8">
                      <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Temporanea Caso Morte (TCM)</div>
                      <div className="mt-5 grid sm:grid-cols-2 gap-5">
                        <div><Label>Capitale desiderato (facoltativo)</Label><Input testid="vita-tcm-capitale" value={form.vita.tcm_capitale} onChange={(e) => setField("vita.tcm_capitale", e.target.value)} placeholder="Es. 200.000 €" /></div>
                        <div><Label>Durata indicativa (facoltativa)</Label><Input testid="vita-tcm-durata" value={form.vita.tcm_durata} onChange={(e) => setField("vita.tcm_durata", e.target.value)} placeholder="Es. 20 anni" /></div>
                      </div>
                    </div>
                  )}

                  {/* Sezione 4 — Condizionale Previdenza/Multiramo */}
                  {(form.vita.prodotti.includes("Previdenza (FPA/PIP)") || form.vita.prodotti.includes("Investimento Multiramo")) && (
                    <div className="mt-8">
                      <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Modalità di versamento</div>
                      <div className="mt-5 grid gap-5">
                        <div>
                          <Label>Come pensi di effettuare il versamento?</Label>
                          <Select testid="vita-versamento" value={form.vita.versamento_tipo} onChange={(e) => setField("vita.versamento_tipo", e.target.value)}>
                            <option value="">Seleziona…</option>
                            <option value="periodico">Versamento periodico</option>
                            <option value="capitale_iniziale">Capitale iniziale</option>
                            <option value="entrambi">Entrambi</option>
                            <option value="non_so">Non lo so / Vorrei essere consigliato</option>
                          </Select>
                        </div>
                        {(form.vita.versamento_tipo === "periodico" || form.vita.versamento_tipo === "entrambi") && (
                          <div><Label>Importo periodico indicativo (facoltativo)</Label><Input testid="vita-imp-periodico" value={form.vita.importo_periodico} onChange={(e) => setField("vita.importo_periodico", e.target.value)} placeholder="Es. 100 €/mese" /></div>
                        )}
                        {(form.vita.versamento_tipo === "capitale_iniziale" || form.vita.versamento_tipo === "entrambi") && (
                          <div><Label>Capitale iniziale indicativo (facoltativo)</Label><Input testid="vita-cap-iniziale" value={form.vita.capitale_iniziale} onChange={(e) => setField("vita.capitale_iniziale", e.target.value)} placeholder="Es. 20.000 €" /></div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Sezione 5 — Note */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Note aggiuntive</div>
                    <div className="mt-5">
                      <Textarea testid="vita-note" value={form.vita.note} onChange={(e) => setField("vita.note", e.target.value)} placeholder="Es. Ho già una polizza aziendale, sto valutando il TFR, altre esigenze…" />
                    </div>
                  </div>
                </div>
              )}

              {showRcPro && (
                <div>
                  <h2 className="font-serif text-2xl text-slate-900">Preventivo RC Professionale</h2>
                  <p className="text-sm text-slate-500 mt-2">Aiutaci a costruire la copertura più adatta alla tua attività.</p>

                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Dati del richiedente</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div><Label>Nome</Label><Input testid="rcpro-nome" value={form.rc_pro.nome} onChange={(e) => setField("rc_pro.nome", e.target.value)} placeholder="Nome" /></div>
                      <div><Label>Cognome</Label><Input testid="rcpro-cognome" value={form.rc_pro.cognome} onChange={(e) => setField("rc_pro.cognome", e.target.value)} placeholder="Cognome" /></div>
                      <div><Label>Data di nascita</Label><Input testid="rcpro-nascita" type="date" value={form.rc_pro.data_nascita} onChange={(e) => setField("rc_pro.data_nascita", e.target.value)} /></div>
                      <div><Label required>Codice fiscale</Label><Input testid="rcpro-cf" value={form.rc_pro.codice_fiscale} onChange={(e) => setField("rc_pro.codice_fiscale", e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501U" maxLength={16} /></div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Dati professionali</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div className="sm:col-span-2"><Label>Professione / attività</Label><Input testid="rcpro-professione" value={form.rc_pro.professione} onChange={(e) => setField("rc_pro.professione", e.target.value)} placeholder="Es. Avvocato, Commercialista, Consulente IT…" /></div>
                      <div className="sm:col-span-2"><Label>Ragione sociale / denominazione (facoltativa)</Label><Input testid="rcpro-ragione" value={form.rc_pro.ragione_sociale} onChange={(e) => setField("rc_pro.ragione_sociale", e.target.value)} placeholder="Es. Studio Legale Rossi Srl" /></div>
                      <div>
                        <Label>Partita IVA</Label>
                        <Select testid="rcpro-piva" value={form.rc_pro.partita_iva} onChange={(e) => setField("rc_pro.partita_iva", e.target.value)}>
                          <option value="">—</option>
                          <option value="si">Sì</option>
                          <option value="no">No</option>
                        </Select>
                      </div>
                      <div><Label>Fatturato annuo indicativo</Label><Input testid="rcpro-fatturato" value={form.rc_pro.fatturato_annuo} onChange={(e) => setField("rc_pro.fatturato_annuo", e.target.value)} placeholder="Es. 80.000 €" /></div>
                      <div><Label>Anni di attività</Label><Input testid="rcpro-anni" value={form.rc_pro.anni_attivita} onChange={(e) => setField("rc_pro.anni_attivita", e.target.value)} placeholder="Es. 5" /></div>
                      <div>
                        <Label>Iscrizione ad albo/ordine</Label>
                        <Select testid="rcpro-albo" value={form.rc_pro.iscrizione_albo} onChange={(e) => setField("rc_pro.iscrizione_albo", e.target.value)}>
                          <option value="">—</option>
                          <option value="si">Sì</option>
                          <option value="no">No</option>
                        </Select>
                      </div>
                      <div><Label>Massimale desiderato</Label><Input testid="rcpro-massimale" value={form.rc_pro.massimale} onChange={(e) => setField("rc_pro.massimale", e.target.value)} placeholder="Es. 1.000.000 €" /></div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Garanzie d'interesse</div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {RC_PRO_GARANZIE.map((g) => {
                        const active = form.rc_pro.garanzie.includes(g);
                        return (
                          <button key={g} type="button" data-testid={`rcpro-gar-${g}`} onClick={() => toggleInArray("rc_pro", "garanzie", g)}
                            className={`px-4 h-10 text-sm border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`}
                          >{g}</button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Note aggiuntive</div>
                    <div className="mt-5">
                      <Textarea testid="rcpro-note" value={form.rc_pro.note} onChange={(e) => setField("rc_pro.note", e.target.value)} placeholder="Esigenze particolari o altre informazioni utili…" />
                    </div>
                  </div>
                </div>
              )}

              {showMonopattino && (
                <div>
                  <h2 className="font-serif text-2xl text-slate-900">Preventivo Monopattino Elettrico</h2>
                  <p className="text-sm text-slate-500 mt-2">Poche informazioni essenziali per predisporre la proposta.</p>

                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Decorrenza</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div>
                        <Label>Data decorrenza desiderata</Label>
                        <Input testid="mono-decorrenza" type="date" min={todayISO()} value={form.monopattino.decorrenza} onChange={(e) => setField("monopattino.decorrenza", e.target.value)} />
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Proprietario del monopattino</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div><Label>Nome</Label><Input testid="mono-nome" value={form.monopattino.nome} onChange={(e) => setField("monopattino.nome", e.target.value)} placeholder="Nome" /></div>
                      <div><Label>Cognome</Label><Input testid="mono-cognome" value={form.monopattino.cognome} onChange={(e) => setField("monopattino.cognome", e.target.value)} placeholder="Cognome" /></div>
                      <div><Label>Data di nascita</Label><Input testid="mono-nascita" type="date" value={form.monopattino.data_nascita} onChange={(e) => setField("monopattino.data_nascita", e.target.value)} /></div>
                      <div><Label required>Codice fiscale</Label><Input testid="mono-cf" value={form.monopattino.codice_fiscale} onChange={(e) => setField("monopattino.codice_fiscale", e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501U" maxLength={16} /></div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Dati del monopattino</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div><Label>Marca</Label><Input testid="mono-marca" value={form.monopattino.marca} onChange={(e) => setField("monopattino.marca", e.target.value)} placeholder="Es. Xiaomi" /></div>
                      <div><Label>Modello</Label><Input testid="mono-modello" value={form.monopattino.modello} onChange={(e) => setField("monopattino.modello", e.target.value)} placeholder="Es. Mi 4 Pro" /></div>
                      <div><Label>Targa / contrassegno identificativo (se assegnato)</Label><Input testid="mono-targa" value={form.monopattino.targa} onChange={(e) => setField("monopattino.targa", e.target.value.toUpperCase())} placeholder="Facoltativo" /></div>
                      <div><Label>Numero di serie / seriale (se conosciuto)</Label><Input testid="mono-seriale" value={form.monopattino.numero_serie} onChange={(e) => setField("monopattino.numero_serie", e.target.value)} placeholder="Facoltativo" /></div>
                      <div><Label>Anno di acquisto</Label><Input testid="mono-anno" type="number" min="2000" max="2100" value={form.monopattino.anno_acquisto} onChange={(e) => setField("monopattino.anno_acquisto", e.target.value)} placeholder="Es. 2024" /></div>
                      <div><Label>Comune di utilizzo prevalente</Label><Input testid="mono-comune" value={form.monopattino.comune} onChange={(e) => setField("monopattino.comune", e.target.value)} placeholder="Es. Milano" /></div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Garanzie d'interesse</div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {MONOPATTINO_GARANZIE.map((g) => {
                        const active = form.monopattino.garanzie.includes(g);
                        return (
                          <button key={g} type="button" data-testid={`mono-gar-${g}`} onClick={() => toggleInArray("monopattino", "garanzie", g)}
                            className={`px-4 h-10 text-sm border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`}
                          >{g}</button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Note aggiuntive</div>
                    <div className="mt-5">
                      <Textarea testid="mono-note" value={form.monopattino.note} onChange={(e) => setField("monopattino.note", e.target.value)} placeholder="Altre informazioni utili…" />
                    </div>
                  </div>
                </div>
              )}

              {showMoto && (
                <div>
                  <h2 className="font-serif text-2xl text-slate-900">Preventivo Moto / Scooter</h2>
                  <p className="text-sm text-slate-500 mt-2">
                    Compila i campi che conosci — non è tutto obbligatorio. I dati di sinistrosità e classe di merito verranno
                    verificati direttamente dall'agenzia sulle banche dati assicurative.
                  </p>

                  {/* Sezione 1 — Situazione assicurativa */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Situazione assicurativa</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div>
                        <Label>Data decorrenza desiderata</Label>
                        <Input testid="moto-decorrenza" type="date" min={todayISO()} value={form.moto.decorrenza} onChange={(e) => setField("moto.decorrenza", e.target.value)} />
                      </div>
                      <div>
                        <Label>Situazione del veicolo</Label>
                        <Select testid="moto-situazione" value={form.moto.situazione_veicolo} onChange={(e) => setField("moto.situazione_veicolo", e.target.value)}>
                          <option value="">Seleziona…</option>
                          <option value="gia_assicurato">Già assicurato</option>
                          <option value="passaggio_proprieta">Appena acquistato / passaggio di proprietà</option>
                          <option value="nuovo_da_immatricolare">Moto nuova da immatricolare</option>
                          <option value="usata_da_acquistare">Moto usata da acquistare</option>
                          <option value="altro">Altro</option>
                        </Select>
                      </div>
                      {form.moto.situazione_veicolo === "nuovo_da_immatricolare" && (
                        <div>
                          <Label>Data prevista consegna (facoltativa)</Label>
                          <Input testid="moto-consegna" type="date" min={todayISO()} value={form.moto.data_consegna_prevista} onChange={(e) => setField("moto.data_consegna_prevista", e.target.value)} />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sezione 2 — Dati veicolo */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Dati veicolo</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      {form.moto.situazione_veicolo !== "nuovo_da_immatricolare" && (
                        <div><Label>Targa</Label><Input testid="moto-targa" value={form.moto.targa} onChange={(e) => setField("moto.targa", e.target.value.toUpperCase())} placeholder="AB12345" /></div>
                      )}
                      <div><Label required>Marca</Label><Input testid="moto-marca" value={form.moto.marca} onChange={(e) => setField("moto.marca", e.target.value)} placeholder="Es. Honda, Yamaha, Piaggio…" /></div>
                      <div><Label required>Modello</Label><Input testid="moto-modello" value={form.moto.modello} onChange={(e) => setField("moto.modello", e.target.value)} placeholder="Es. CBR 600" /></div>
                      <div><Label>Anno di immatricolazione</Label><Input testid="moto-anno" type="number" min="1970" max="2100" value={form.moto.anno} onChange={(e) => setField("moto.anno", e.target.value)} placeholder="Es. 2019" /></div>
                      <div><Label>Km annui stimati</Label><Input testid="moto-km" type="number" value={form.moto.km_annui} onChange={(e) => setField("moto.km_annui", e.target.value)} placeholder="Es. 5000" /></div>
                    </div>
                  </div>

                  {/* Sezione 3 — Classe di merito */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Classe di merito</div>
                    <div className="mt-5 grid gap-5">
                      <div>
                        <Label>Hai un veicolo dal quale recuperare la classe di merito?</Label>
                        <Select testid="moto-provenienza-cu" value={form.moto.provenienza_cu} onChange={(e) => setField("moto.provenienza_cu", e.target.value)}>
                          <option value="">Seleziona…</option>
                          <option value="propria">Sì, un veicolo di mia proprietà</option>
                          <option value="familiare">Sì, un veicolo di un familiare convivente</option>
                          <option value="nessuna">No</option>
                          <option value="non_so">Non lo so</option>
                        </Select>
                      </div>
                      {form.moto.provenienza_cu === "propria" && (
                        <div className="grid sm:grid-cols-2 gap-5">
                          <div><Label>Targa del veicolo</Label><Input testid="moto-cu-targa" value={form.moto.targa_recupero_cu} onChange={(e) => setField("moto.targa_recupero_cu", e.target.value.toUpperCase())} placeholder="AB12345" /></div>
                          <div><Label>Classe di merito attuale (facoltativa)</Label><Input testid="moto-classe-merito" value={form.moto.classe_merito} onChange={(e) => setField("moto.classe_merito", e.target.value)} placeholder="Es. CU/1" /></div>
                        </div>
                      )}
                      {form.moto.provenienza_cu === "familiare" && (
                        <div className="grid sm:grid-cols-2 gap-5">
                          <div><Label>Targa del veicolo del familiare</Label><Input testid="moto-cu-targa" value={form.moto.targa_recupero_cu} onChange={(e) => setField("moto.targa_recupero_cu", e.target.value.toUpperCase())} placeholder="AB12345" /></div>
                          <div><Label>Nome e cognome del familiare</Label><Input testid="moto-cu-familiare" value={form.moto.familiare_nome} onChange={(e) => setField("moto.familiare_nome", e.target.value)} placeholder="Mario Rossi" /></div>
                          <div className="sm:col-span-2">
                            <Label>Grado di parentela</Label>
                            <Select testid="moto-cu-parentela" value={form.moto.familiare_parentela} onChange={(e) => setField("moto.familiare_parentela", e.target.value)}>
                              <option value="">Seleziona…</option>
                              <option value="coniuge">Coniuge / Convivente</option>
                              <option value="genitore">Genitore</option>
                              <option value="figlio">Figlio/a</option>
                              <option value="fratello">Fratello / Sorella</option>
                              <option value="altro">Altro convivente</option>
                            </Select>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sezione 4 — Conducente */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Conducente</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div><Label>Conducente principale</Label><Input testid="moto-conducente" value={form.moto.conducente_nome} onChange={(e) => setField("moto.conducente_nome", e.target.value)} placeholder="Nome Cognome" /></div>
                      <div><Label>Data di nascita conducente</Label><Input testid="moto-nascita" type="date" value={form.moto.conducente_data_nascita} onChange={(e) => setField("moto.conducente_data_nascita", e.target.value)} /></div>
                      <div>
                        <Label>Tipo di guida</Label>
                        <Select testid="moto-guida" value={form.moto.guida} onChange={(e) => setField("moto.guida", e.target.value)}>
                          <option value="">Seleziona…</option>
                          <option value="libera">Guida libera</option>
                          <option value="esperta">Guida esperta</option>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Sezione — Proprietario del veicolo */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Proprietario del veicolo</div>
                    <p className="mt-3 text-sm text-slate-500">Se coincide con il conducente principale puoi ripetere i dati.</p>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div><Label>Nome e cognome</Label><Input testid="moto-prop-nome" value={form.moto.proprietario_nome} onChange={(e) => setField("moto.proprietario_nome", e.target.value)} placeholder="Nome Cognome" /></div>
                      <div><Label>Data di nascita</Label><Input testid="moto-prop-nascita" type="date" value={form.moto.proprietario_data_nascita} onChange={(e) => setField("moto.proprietario_data_nascita", e.target.value)} /></div>
                      <div className="sm:col-span-2"><Label required>Codice fiscale</Label><Input testid="moto-prop-cf" value={form.moto.proprietario_codice_fiscale} onChange={(e) => setField("moto.proprietario_codice_fiscale", e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501U" maxLength={16} /></div>
                    </div>
                  </div>

                  {/* Sezione 5 — Garanzie richieste */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Garanzie richieste</div>
                    <p className="mt-3 text-sm text-slate-500">Seleziona una o più garanzie di tuo interesse. RCA è la copertura obbligatoria di base.</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {MOTO_GARANZIE.map((g) => {
                        const active = form.moto.garanzie.includes(g);
                        return (
                          <button key={g} type="button" data-testid={`moto-gar-${g}`} onClick={() => toggleInArray("moto", "garanzie", g)}
                            className={`px-4 h-10 text-sm border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`}
                          >{g}</button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sezione 6 — Note */}
                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Note aggiuntive</div>
                    <div className="mt-5">
                      <Textarea testid="moto-note" value={form.moto.note} onChange={(e) => setField("moto.note", e.target.value)} placeholder="Altre informazioni utili…" />
                    </div>
                  </div>
                </div>
              )}

              {showViaggi && (
                <div>
                  <h2 className="font-serif text-2xl text-slate-900">Preventivo Viaggi</h2>
                  <p className="text-sm text-slate-500 mt-2">Identifichiamo il richiedente e i viaggiatori per predisporre la copertura più adatta.</p>

                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Dati del richiedente</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div><Label>Nome</Label><Input testid="viaggi-nome" value={form.viaggi.nome} onChange={(e) => setField("viaggi.nome", e.target.value)} placeholder="Nome" /></div>
                      <div><Label>Cognome</Label><Input testid="viaggi-cognome" value={form.viaggi.cognome} onChange={(e) => setField("viaggi.cognome", e.target.value)} placeholder="Cognome" /></div>
                      <div><Label>Data di nascita</Label><Input testid="viaggi-nascita" type="date" value={form.viaggi.data_nascita} onChange={(e) => setField("viaggi.data_nascita", e.target.value)} /></div>
                      <div><Label required>Codice fiscale</Label><Input testid="viaggi-cf" value={form.viaggi.codice_fiscale} onChange={(e) => setField("viaggi.codice_fiscale", e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501U" maxLength={16} /></div>
                      <div className="sm:col-span-2">
                        <Label>Il richiedente è anche uno dei viaggiatori?</Label>
                        <Select testid="viaggi-richiedente-v" value={form.viaggi.richiedente_viaggiatore} onChange={(e) => {
                          const v = e.target.value;
                          setField("viaggi.richiedente_viaggiatore", v);
                          // Re-sync viaggiatori con nuovi flag
                          setTimeout(() => syncViaggiatori(form.viaggi.numero_viaggiatori, undefined), 0);
                        }}>
                          <option value="">Seleziona…</option>
                          <option value="si">Sì</option>
                          <option value="no">No</option>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Dati del viaggio</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div className="sm:col-span-2"><Label>Destinazione</Label><Input testid="viaggi-dest" value={form.viaggi.destinazione} onChange={(e) => setField("viaggi.destinazione", e.target.value)} placeholder="Es. Grecia, USA, Sud-est asiatico…" /></div>
                      <div><Label>Data partenza</Label><Input testid="viaggi-partenza" type="date" min={todayISO()} value={form.viaggi.data_partenza} onChange={(e) => setField("viaggi.data_partenza", e.target.value)} /></div>
                      <div><Label>Data ritorno</Label><Input testid="viaggi-ritorno" type="date" min={form.viaggi.data_partenza || todayISO()} value={form.viaggi.data_ritorno} onChange={(e) => setField("viaggi.data_ritorno", e.target.value)} /></div>
                      <div>
                        <Label>Numero viaggiatori</Label>
                        <Input testid="viaggi-num" type="number" min="1" max="20" value={form.viaggi.numero_viaggiatori} onChange={(e) => syncViaggiatori(e.target.value)} placeholder="Es. 2" />
                      </div>
                      <div>
                        <Label>Tipologia viaggio</Label>
                        <Select testid="viaggi-tipo" value={form.viaggi.tipologia} onChange={(e) => setField("viaggi.tipologia", e.target.value)}>
                          <option value="">Seleziona…</option>
                          <option value="vacanza">Vacanza</option>
                          <option value="lavoro">Lavoro</option>
                          <option value="studio">Studio</option>
                          <option value="sport">Sport</option>
                        </Select>
                      </div>
                      <div className="sm:col-span-2"><Label>Valore indicativo del viaggio (facoltativo)</Label><Input testid="viaggi-valore" value={form.viaggi.valore_viaggio} onChange={(e) => setField("viaggi.valore_viaggio", e.target.value)} placeholder="Es. 3.500 €" /></div>
                    </div>
                  </div>

                  {(form.viaggi.viaggiatori || []).length > 0 && (
                    <div className="mt-8">
                      <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Viaggiatori</div>
                      <div className="mt-5 space-y-6">
                        {(form.viaggi.viaggiatori || []).map((v, i) => (
                          <div key={i} className="border border-slate-200 p-4 bg-[#FAF9F6]">
                            <div className="text-xs font-medium uppercase tracking-wide text-slate-700 mb-3">Viaggiatore {i + 1}{i === 0 && form.viaggi.richiedente_viaggiatore === "si" ? " (richiedente)" : ""}</div>
                            <div className="grid sm:grid-cols-2 gap-4">
                              <div><Label>Nome</Label><Input testid={`viaggiatore-${i}-nome`} value={v.nome || ""} onChange={(e) => setViaggiatoreField(i, "nome", e.target.value)} placeholder="Nome" /></div>
                              <div><Label>Cognome</Label><Input testid={`viaggiatore-${i}-cognome`} value={v.cognome || ""} onChange={(e) => setViaggiatoreField(i, "cognome", e.target.value)} placeholder="Cognome" /></div>
                              <div><Label>Data di nascita</Label><Input testid={`viaggiatore-${i}-nascita`} type="date" value={v.data_nascita || ""} onChange={(e) => setViaggiatoreField(i, "data_nascita", e.target.value)} /></div>
                              <div><Label>Codice fiscale</Label><Input testid={`viaggiatore-${i}-cf`} value={v.codice_fiscale || ""} onChange={(e) => setViaggiatoreField(i, "codice_fiscale", e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501U" maxLength={16} /></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Garanzie d'interesse</div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {VIAGGI_GARANZIE.map((g) => {
                        const active = form.viaggi.garanzie.includes(g);
                        return (
                          <button key={g} type="button" data-testid={`viaggi-gar-${g}`} onClick={() => toggleInArray("viaggi", "garanzie", g)}
                            className={`px-4 h-10 text-sm border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`}
                          >{g}</button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Note aggiuntive</div>
                    <div className="mt-5">
                      <Textarea testid="viaggi-note" value={form.viaggi.note} onChange={(e) => setField("viaggi.note", e.target.value)} placeholder="Altre informazioni utili sul viaggio…" />
                    </div>
                  </div>
                </div>
              )}

              {showTutelaLegale && (
                <div>
                  <h2 className="font-serif text-2xl text-slate-900">Tutela Legale</h2>
                  <p className="text-sm text-slate-500 mt-2">Dicci l'ambito di tutela desiderato — bastano poche informazioni.</p>
                  <div className="mt-6 grid gap-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <Label>Persona fisica o azienda</Label>
                        <Select testid="tl-soggetto" value={form.tutela_legale.soggetto} onChange={(e) => setField("tutela_legale.soggetto", e.target.value)}>
                          <option value="">Seleziona…</option>
                          <option value="persona_fisica">Persona fisica</option>
                          <option value="azienda">Azienda</option>
                        </Select>
                      </div>
                      <div>
                        <Label>Copertura</Label>
                        <Select testid="tl-copertura" value={form.tutela_legale.copertura} onChange={(e) => setField("tutela_legale.copertura", e.target.value)}>
                          <option value="">Seleziona…</option>
                          <option value="individuale">Individuale</option>
                          <option value="nucleo_azienda">Nucleo familiare / Azienda</option>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Ambito di tutela (seleziona uno o più)</Label>
                      <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {TUTELA_AMBITI.map((g) => {
                          const active = form.tutela_legale.ambito.includes(g);
                          return (
                            <button key={g} type="button" data-testid={`tl-amb-${g}`} onClick={() => toggleInArray("tutela_legale", "ambito", g)}
                              className={`px-4 h-11 text-sm border text-left transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`}
                            >{g}</button>
                          );
                        })}
                      </div>
                    </div>
                    <div><Label>Necessità specifica</Label><Textarea testid="tl-nec" value={form.tutela_legale.necessita} onChange={(e) => setField("tutela_legale.necessita", e.target.value)} placeholder="Es. Contenziosi con inquilini, controversie sul lavoro…" /></div>
                    <div><Label>Note aggiuntive</Label><Textarea testid="tl-note" value={form.tutela_legale.note} onChange={(e) => setField("tutela_legale.note", e.target.value)} placeholder="Altre informazioni utili…" /></div>
                  </div>
                </div>
              )}

              {showPet && (
                <div>
                  <h2 className="font-serif text-2xl text-slate-900">Preventivo Cani e Gatti</h2>
                  <p className="text-sm text-slate-500 mt-2">Identifichiamo proprietario e animale per predisporre la copertura più adatta.</p>

                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Proprietario</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div><Label>Nome</Label><Input testid="pet-prop-nome" value={form.pet.proprietario_nome} onChange={(e) => setField("pet.proprietario_nome", e.target.value)} placeholder="Nome" /></div>
                      <div><Label>Cognome</Label><Input testid="pet-prop-cognome" value={form.pet.proprietario_cognome} onChange={(e) => setField("pet.proprietario_cognome", e.target.value)} placeholder="Cognome" /></div>
                      <div><Label>Data di nascita</Label><Input testid="pet-prop-nascita" type="date" value={form.pet.proprietario_data_nascita} onChange={(e) => setField("pet.proprietario_data_nascita", e.target.value)} /></div>
                      <div><Label required>Codice fiscale</Label><Input testid="pet-prop-cf" value={form.pet.proprietario_codice_fiscale} onChange={(e) => setField("pet.proprietario_codice_fiscale", e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501U" maxLength={16} /></div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Animale</div>
                    <div className="mt-5 grid sm:grid-cols-2 gap-5">
                      <div>
                        <Label>Specie</Label>
                        <Select testid="pet-animale" value={form.pet.animale} onChange={(e) => setField("pet.animale", e.target.value)}>
                          <option value="">Seleziona…</option>
                          <option value="cane">Cane</option>
                          <option value="gatto">Gatto</option>
                        </Select>
                      </div>
                      <div><Label>Nome</Label><Input testid="pet-nome" value={form.pet.nome} onChange={(e) => setField("pet.nome", e.target.value)} placeholder="Es. Luna" /></div>
                      <div><Label>Razza</Label><Input testid="pet-razza" value={form.pet.razza} onChange={(e) => setField("pet.razza", e.target.value)} placeholder="Es. Labrador, Meticcio…" /></div>
                      <div><Label>Data di nascita (se conosciuta)</Label><Input testid="pet-nascita" type="date" value={form.pet.data_nascita} onChange={(e) => setField("pet.data_nascita", e.target.value)} /></div>
                      <div><Label>Età (se la data non è conosciuta)</Label><Input testid="pet-eta" value={form.pet.eta} onChange={(e) => setField("pet.eta", e.target.value)} placeholder="Es. 3 anni" /></div>
                      <div>
                        <Label>Sesso</Label>
                        <Select testid="pet-sesso" value={form.pet.sesso} onChange={(e) => setField("pet.sesso", e.target.value)}>
                          <option value="">Seleziona…</option>
                          <option value="maschio">Maschio</option>
                          <option value="femmina">Femmina</option>
                        </Select>
                      </div>
                      <div>
                        <Label>Microchip presente?</Label>
                        <Select testid="pet-mc-presente" value={form.pet.microchip_presente} onChange={(e) => setField("pet.microchip_presente", e.target.value)}>
                          <option value="">Seleziona…</option>
                          <option value="si">Sì</option>
                          <option value="no">No</option>
                          <option value="non_so">Non lo so</option>
                        </Select>
                      </div>
                      {form.pet.microchip_presente === "si" && (
                        <div><Label>Numero microchip (facoltativo)</Label><Input testid="pet-mc-numero" value={form.pet.microchip_numero} onChange={(e) => setField("pet.microchip_numero", e.target.value)} placeholder="15 cifre" /></div>
                      )}
                      <div className="sm:col-span-2"><Label>Stato di salute generale (facoltativo)</Label><Input testid="pet-salute" value={form.pet.stato_salute} onChange={(e) => setField("pet.stato_salute", e.target.value)} placeholder="Es. Buono" /></div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Garanzie d'interesse</div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {PET_GARANZIE.map((g) => {
                        const active = form.pet.garanzie.includes(g);
                        return (
                          <button key={g} type="button" data-testid={`pet-gar-${g}`} onClick={() => toggleInArray("pet", "garanzie", g)}
                            className={`px-4 h-10 text-sm border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-900"}`}
                          >{g}</button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-8">
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 pb-2">Note aggiuntive</div>
                    <div className="mt-5">
                      <Textarea testid="pet-note" value={form.pet.note} onChange={(e) => setField("pet.note", e.target.value)} placeholder="Vaccinazioni, sterilizzazione, altre informazioni…" />
                    </div>
                  </div>
                </div>
              )}

              {/* Attachments — optional for all types */}
              <div>
                <h2 className="font-serif text-2xl text-slate-900">Documenti (facoltativi)</h2>
                {(showAuto || showMoto) && (
                  <>
                    <p className="text-sm text-slate-500 mt-2">
                      Se ti fa comodo puoi già allegare qualcosa — <strong>niente è obbligatorio</strong>, la richiesta si può completare senza documenti.
                    </p>
                    <ul className="mt-3 text-sm text-slate-600 list-disc pl-5 space-y-1">
                      <li>Carta d'identità</li>
                      <li>Codice fiscale / Tessera sanitaria</li>
                      <li>Libretto di circolazione</li>
                      {(form.auto.provenienza_cu === "propria" || form.auto.provenienza_cu === "familiare" || form.moto.provenienza_cu === "propria" || form.moto.provenienza_cu === "familiare") && (
                        <li>Libretto del veicolo dal quale recuperare la classe di merito</li>
                      )}
                    </ul>
                  </>
                )}
                {showCasa && (
                  <>
                    <p className="text-sm text-slate-500 mt-2">
                      Se ti fa comodo puoi già allegare qualcosa — <strong>niente è obbligatorio</strong>.
                    </p>
                    <ul className="mt-3 text-sm text-slate-600 list-disc pl-5 space-y-1">
                      <li>Carta d'identità</li>
                      <li>Codice fiscale</li>
                      <li>Visura catastale</li>
                      <li>Eventuale polizza precedente</li>
                    </ul>
                  </>
                )}
                {showVita && (
                  <>
                    <p className="text-sm text-slate-500 mt-2">
                      Se ti fa comodo puoi già allegare qualcosa — <strong>niente è obbligatorio</strong>.
                    </p>
                    <ul className="mt-3 text-sm text-slate-600 list-disc pl-5 space-y-1">
                      <li>Carta d'identità</li>
                      <li>Codice fiscale</li>
                    </ul>
                  </>
                )}
                {showInfortuni && (
                  <>
                    <p className="text-sm text-slate-500 mt-2">
                      Se ti fa comodo puoi già allegare qualcosa — <strong>niente è obbligatorio</strong>.
                    </p>
                    <ul className="mt-3 text-sm text-slate-600 list-disc pl-5 space-y-1">
                      <li>Carta d'identità</li>
                      <li>Codice fiscale</li>
                      <li>Eventuale polizza precedente</li>
                    </ul>
                  </>
                )}
                {showRcPro && (
                  <>
                    <p className="text-sm text-slate-500 mt-2">
                      Se ti fa comodo puoi già allegare qualcosa — <strong>niente è obbligatorio</strong>.
                    </p>
                    <ul className="mt-3 text-sm text-slate-600 list-disc pl-5 space-y-1">
                      <li>Carta d'identità</li>
                      <li>Codice fiscale / Tessera sanitaria</li>
                      <li>Eventuale visura camerale</li>
                      <li>Eventuale polizza precedente</li>
                    </ul>
                  </>
                )}
                {showMonopattino && (
                  <>
                    <p className="text-sm text-slate-500 mt-2">
                      Se ti fa comodo puoi già allegare qualcosa — <strong>niente è obbligatorio</strong>.
                    </p>
                    <ul className="mt-3 text-sm text-slate-600 list-disc pl-5 space-y-1">
                      <li>Carta d'identità</li>
                      <li>Codice fiscale / Tessera sanitaria</li>
                      <li>Eventuale documento del monopattino</li>
                      <li>Eventuale foto del monopattino</li>
                      <li>Eventuale documento di acquisto</li>
                    </ul>
                  </>
                )}
                {showViaggi && (
                  <>
                    <p className="text-sm text-slate-500 mt-2">
                      Se ti fa comodo puoi già allegare qualcosa — <strong>niente è obbligatorio</strong>.
                    </p>
                    <ul className="mt-3 text-sm text-slate-600 list-disc pl-5 space-y-1">
                      <li>Carta d'identità / Passaporto</li>
                      <li>Codice fiscale</li>
                      <li>Conferma prenotazione</li>
                      <li>Biglietti di viaggio</li>
                      <li>Eventuale polizza precedente</li>
                    </ul>
                  </>
                )}
                {showPet && (
                  <>
                    <p className="text-sm text-slate-500 mt-2">
                      Se ti fa comodo puoi già allegare qualcosa — <strong>niente è obbligatorio</strong>.
                    </p>
                    <ul className="mt-3 text-sm text-slate-600 list-disc pl-5 space-y-1">
                      <li>Carta d'identità del proprietario</li>
                      <li>Codice fiscale / Tessera sanitaria</li>
                      <li>Libretto sanitario dell'animale</li>
                      <li>Documentazione microchip</li>
                      <li>Eventuale polizza precedente</li>
                    </ul>
                  </>
                )}
                {!showAuto && !showMoto && !showCasa && !showVita && !showInfortuni && !showRcPro && !showMonopattino && !showViaggi && !showPet && (
                  <p className="text-sm text-slate-500 mt-2">
                    Allega eventuali foto/documenti utili.
                  </p>
                )}
                <p className="mt-3 text-xs text-slate-500">
                  Formati accettati: PDF, JPG, PNG · max {MAX_FILES} file, 5 MB ciascuno.
                </p>
                <div className="mt-5">
                  <input
                    ref={fileInputRef}
                    data-testid="attach-input"
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                  />
                  <button
                    type="button"
                    data-testid="attach-button"
                    disabled={attachments.length >= MAX_FILES}
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-2 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed h-11 px-5 text-sm"
                  >
                    <Upload size={16} /> Carica documenti
                  </button>

                  {attachments.length > 0 && (
                    <ul data-testid="attach-list" className="mt-5 divide-y divide-slate-200 border border-slate-200">
                      {attachments.map((a, i) => (
                        <li key={i} className="flex items-center justify-between px-4 py-3 text-sm">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="w-8 h-8 flex items-center justify-center bg-slate-100 text-slate-700 shrink-0">
                              <FileText size={14} />
                            </span>
                            <div className="min-w-0">
                              <div className="truncate text-slate-900" title={a.filename}>{a.filename}</div>
                              <div className="text-xs text-slate-500">{fmtSize(a.size)} · {a.content_type}</div>
                            </div>
                          </div>
                          <button
                            type="button"
                            data-testid={`attach-remove-${i}`}
                            onClick={() => removeAttachment(i)}
                            className="text-slate-500 hover:text-red-600 p-1"
                            aria-label="Rimuovi allegato"
                          >
                            <X size={16} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 2 — Contact */}
          {step === 2 && (
            <div>
              <h2 className="font-serif text-2xl text-slate-900">I tuoi contatti</h2>
              <p className="text-sm text-slate-500 mt-2">Useremo questi dati esclusivamente per rispondere alla tua richiesta.</p>
              <div className="mt-8 grid sm:grid-cols-2 gap-5">
                <div><Label required>Nome</Label><Input testid="contact-nome" value={form.nome} onChange={(e) => setField("nome", e.target.value)} /></div>
                <div><Label required>Cognome</Label><Input testid="contact-cognome" value={form.cognome} onChange={(e) => setField("cognome", e.target.value)} /></div>
                <div><Label required>Email</Label><Input testid="contact-email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} /></div>
                <div><Label required>Telefono</Label><Input testid="contact-telefono" value={form.telefono} onChange={(e) => setField("telefono", e.target.value)} placeholder="+39 ..." /></div>
                <div className="sm:col-span-2"><Label>Messaggio (opzionale)</Label><Textarea testid="contact-note" value={form.note} onChange={(e) => setField("note", e.target.value)} /></div>
                <label className="sm:col-span-2 inline-flex items-start gap-3 text-sm text-slate-700 mt-2">
                  <input type="checkbox" data-testid="contact-privacy" checked={form.privacy_accepted} onChange={(e) => setField("privacy_accepted", e.target.checked)} className="mt-1 w-4 h-4 accent-slate-900" />
                  <span>
                    Ho letto l'
                    <a
                      href="/privacy.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold underline underline-offset-2 decoration-2 text-[#C5A059] hover:text-slate-900"
                      data-testid="privacy-link"
                    >
                      informativa privacy
                    </a>
                    {" "}e acconsento al trattamento dei miei dati
                    al solo scopo di ricevere il preventivo richiesto. <span className="text-[#C5A059]">*</span>
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* STEP 3 — Done */}
          {step === 3 && done && (
            <div data-testid="quote-success" className="py-8 text-center">
              <div className="w-14 h-14 mx-auto bg-slate-900 text-[#C5A059] flex items-center justify-center">
                <Check size={24} />
              </div>
              <h2 className="mt-6 font-serif text-3xl text-slate-900">Richiesta ricevuta</h2>
              <p className="mt-3 text-slate-600 max-w-md mx-auto">
                Grazie {done.nome}! Abbiamo registrato la tua richiesta
                {" "}<span className="font-medium">#{done.id.slice(0, 8).toUpperCase()}</span>.
                Ti contatteremo a breve all'indirizzo <span className="font-medium">{done.email}</span>.
              </p>

              {done.download_token && (
                <div className="mt-8 mx-auto max-w-md bg-[#FAF9F6] border border-slate-200 p-5 text-left">
                  <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500">Riepilogo richiesta</div>
                  <p className="mt-2 text-sm text-slate-600">
                    Scarica un PDF elegante con tutti i dati che hai inserito. Lo useremo come promemoria della tua pratica.
                  </p>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/quotes/download/${done.download_token}/summary.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="success-download-pdf"
                    className="mt-4 inline-flex items-center gap-2 bg-[#C5A059] hover:bg-[#B08D4C] text-slate-900 h-11 px-5 text-sm tracking-wide"
                  >
                    <Download size={16} /> Scarica riepilogo richiesta (PDF)
                  </a>
                  <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                    Il link è valido 24 ore. Il documento è un riepilogo della richiesta inviata e non costituisce un preventivo o una copertura.
                  </p>
                </div>
              )}

              <div className="mt-8 flex justify-center gap-3">
                <Link to="/" className="inline-flex items-center gap-2 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white h-11 px-6 text-sm" data-testid="success-home">
                  Torna alla home
                </Link>
                <button
                onClick={() => { setDone(null); setStep(0); setAttachments([]); setForm((f) => ({ ...f, quote_type: "", note: "", privacy_accepted: false, auto: { targa: "", marca: "", modello: "", anno: "", alimentazione: "", km_annui: "", uso_veicolo: "", antifurto: "", conducente_nome: "", conducente_data_nascita: "", guida: "", proprietario_nome: "", proprietario_data_nascita: "", proprietario_codice_fiscale: "", decorrenza: "", situazione_veicolo: "", data_consegna_prevista: "", provenienza_cu: "", classe_merito: "", targa_recupero_cu: "", familiare_nome: "", familiare_parentela: "", garanzie: ["RCA"], note: "" }, casa: { decorrenza: "", indirizzo: "", citta: "", cap: "", tipo: "", piano: "", mq: "", anno_costruzione: "", uso: "", richiedente_tipo: "", richiedente_nome: "", richiedente_data_nascita: "", richiedente_codice_fiscale: "", coperture: [], antifurto: false, note: "" }, vita: { nome: "", data_nascita: "", codice_fiscale: "", professione: "", prodotti: [], obiettivo: "", tcm_capitale: "", tcm_durata: "", versamento_tipo: "", importo_periodico: "", capitale_iniziale: "", note: "" }, infortuni: { nome: "", data_nascita: "", codice_fiscale: "", professione: "", copertura_per: "", sport_rischio_bool: "", garanzie: [], massimale_invalidita: "", note: "" }, rc_pro: { nome: "", cognome: "", data_nascita: "", codice_fiscale: "", professione: "", ragione_sociale: "", partita_iva: "", fatturato_annuo: "", anni_attivita: "", iscrizione_albo: "", massimale: "", garanzie: [], note: "" }, monopattino: { decorrenza: "", nome: "", cognome: "", data_nascita: "", codice_fiscale: "", marca: "", modello: "", targa: "", numero_serie: "", anno_acquisto: "", comune: "", uso_prevalente: "", marca_modello: "", garanzie: [], note: "" }, moto: { targa: "", marca: "", modello: "", cilindrata: "", anno: "", alimentazione: "", km_annui: "", uso: "", antifurto: "", conducente_nome: "", conducente_data_nascita: "", guida: "", proprietario_nome: "", proprietario_data_nascita: "", proprietario_codice_fiscale: "", decorrenza: "", situazione_veicolo: "", data_consegna_prevista: "", provenienza_cu: "", classe_merito: "", targa_recupero_cu: "", familiare_nome: "", familiare_parentela: "", garanzie: ["RCA"], note: "" }, viaggi: { nome: "", cognome: "", data_nascita: "", codice_fiscale: "", richiedente_viaggiatore: "", destinazione: "", data_partenza: "", data_ritorno: "", numero_viaggiatori: "", tipologia: "", valore_viaggio: "", viaggiatori: [], garanzie: [], note: "" }, tutela_legale: { soggetto: "", ambito: [], necessita: "", copertura: "", note: "" }, pet: { proprietario_nome: "", proprietario_cognome: "", proprietario_data_nascita: "", proprietario_codice_fiscale: "", animale: "", nome: "", razza: "", data_nascita: "", eta: "", sesso: "", microchip_presente: "", microchip_numero: "", stato_salute: "", garanzie: [], note: "" } })); }}
                  className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 h-11 px-6 text-sm"
                  data-testid="success-new"
                >
                  Nuova richiesta
                </button>
              </div>
            </div>
          )}

          {/* Nav */}
          {step < 3 && (
            <div className="mt-10 flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  // Se siamo allo step 1 e siamo arrivati con ?type=X (skip step 0),
                  // "Indietro" torna alla pagina precedente. Altrimenti step normale.
                  if (step === 0 || (step === 1 && initialTypeValid)) {
                    navigate(-1);
                  } else {
                    setStep(step - 1);
                  }
                }}
                className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
                data-testid="quote-back"
              >
                <ChevronLeft size={16} /> Indietro
              </button>
              {step < 2 ? (
                <button
                  type="button"
                  disabled={!canProceed}
                  onClick={() => setStep(step + 1)}
                  className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-white h-11 px-6 text-sm tracking-wide"
                  data-testid="quote-next"
                >
                  Continua <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  type="button"
                  disabled={!canProceed || submitting}
                  onClick={handleSubmit}
                  className="inline-flex items-center gap-2 bg-[#C5A059] hover:bg-[#B08D4C] disabled:opacity-40 text-slate-900 h-11 px-6 text-sm tracking-wide"
                  data-testid="quote-submit"
                >
                  {submitting && <Loader2 size={16} className="animate-spin" />}
                  Invia richiesta
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
